import { injectable } from 'inversify';
import type {
  IPanelPagoRepository,
  PeriodoPago,
  PanelPagoEstado,
  PanelPagoFormData,
  PanelPagoFilters,
} from '@/domain/repositories/IPanelPagoRepository';
import { PanelPago } from '@/domain/entities/PanelPago';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiPanelPagoRepository implements IPanelPagoRepository {
  async getPeriodoPagoByTenantId(tenantId: number | string): Promise<PeriodoPago | null> {
    try {
      const response = await httpClient.get<any>(`/panel-pagos/periodo-pago/${tenantId}`);
      const data = response.data;
      
      return {
        fechaPeriodoPago: data.periodo,
        estadoPagoId: data.estadoPagoId || 1,
        estadoPagoDescripcion: data.estado || 'Pendiente',
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching periodo pago:', error);
      throw error;
    }
  }

  async getAll(filters?: PanelPagoFilters): Promise<PanelPago[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.anio) params.set('anio', filters.anio.toString());
      if (filters?.mes) params.set('mes', filters.mes.toString());
      if (filters?.estadoPagoId) params.set('estadoPagoId', filters.estadoPagoId.toString());

      const query = params.toString();
      const response = await httpClient.get<any[]>(`/panel-pagos${query ? `?${query}` : ''}`);
      return response.data.map(item => this.mapToPanelPago(item));
    } catch (error) {
      console.error('Error fetching panel pagos:', error);
      throw error;
    }
  }

  async getAllEstadosPago(): Promise<PanelPagoEstado[]> {
    try {
      const response = await httpClient.get<any[]>('/panel-pagos/estados-pago');
      return response.data.map(item => ({
        id: item.id,
        estado: item.nombre,
      }));
    } catch (error) {
      console.error('Error fetching estados pago:', error);
      throw error;
    }
  }

  async getByTenantId(tenantId: number | string): Promise<PanelPago[]> {
    try {
      const response = await httpClient.get<any[]>(`/panel-pagos/tenant/${tenantId}`);
      return response.data.map(item => this.mapToPanelPago(item));
    } catch (error) {
      console.error('Error fetching panel pagos by tenant:', error);
      throw error;
    }
  }

  private isTenantGuid(id: number | string | undefined): id is string {
    return typeof id === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);
  }

  async create(data: PanelPagoFormData): Promise<PanelPago> {
    try {
      const body: Record<string, unknown> = {
        fechaPago: data.fechaPago?.toISOString(),
        fechaPeriodoPago: data.fechaPeriodoPago,
        monto: data.monto,
        planId: data.planId,
        medioPago: data.medioPago,
        notas: data.notas,
        nombreArchivoComprobante: data.nombreArchivoComprobante,
        estadoPagoId: data.estadoPagoId,
        deudaAcumulada: data.deudaAcumulada,
        saldarDeuda: data.saldarDeuda,
      };
      if (data.tenantId && this.isTenantGuid(data.tenantId)) {
        body.tenantId = data.tenantId;
      } else if (data.clinicaId != null) {
        body.clinicaId = data.clinicaId;
      }
      const response = await httpClient.post<any>('/panel-pagos', body);

      return this.mapToPanelPago(response.data);
    } catch (error) {
      console.error('Error creating panel pago:', error);
      throw error;
    }
  }

  async updateOrCreate(data: PanelPagoFormData): Promise<PanelPago> {
    try {
      const body: Record<string, unknown> = {
        fechaPago: data.fechaPago?.toISOString(),
        fechaPeriodoPago: data.fechaPeriodoPago,
        monto: data.monto,
        planId: data.planId,
        medioPago: data.medioPago,
        notas: data.notas,
        nombreArchivoComprobante: data.nombreArchivoComprobante,
        estadoPagoId: data.estadoPagoId,
        deudaAcumulada: data.deudaAcumulada,
        saldarDeuda: data.saldarDeuda,
      };
      if (data.tenantId && this.isTenantGuid(data.tenantId)) {
        body.tenantId = data.tenantId;
      } else if (data.clinicaId != null) {
        body.clinicaId = data.clinicaId;
      }
      const response = await httpClient.post<any>('/panel-pagos/update-or-create', body);

      return this.mapToPanelPago(response.data);
    } catch (error) {
      console.error('Error updating or creating panel pago:', error);
      throw error;
    }
  }

  async getByPeriodo(periodo: string): Promise<PanelPago[]> {
    try {
      const response = await httpClient.get<any[]>(`/panel-pagos/periodo/${periodo}`);
      return response.data.map(item => this.mapToPanelPago(item));
    } catch (error) {
      console.error('Error fetching panel pagos by periodo:', error);
      throw error;
    }
  }

  async cancel(id: number): Promise<PanelPago> {
    try {
      const response = await httpClient.post<any>(`/panel-pagos/${id}/cancel`);
      return this.mapToPanelPago(response.data);
    } catch (error) {
      console.error('Error canceling panel pago:', error);
      throw error;
    }
  }

  async getReciboHtml(id: number): Promise<string> {
    const response = await httpClient.get<string>(`/panel-pagos/${id}/recibo`, {
      headers: { Accept: 'text/html' },
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return typeof response.data === 'string' ? response.data : String(response.data ?? '');
  }

  private mapToPanelPago(item: any): PanelPago {
    const tenantIdRaw = item.tenantId ?? item.TenantId;
    const tenantId =
      typeof tenantIdRaw === 'string' && tenantIdRaw.trim()
        ? tenantIdRaw.trim()
        : null;

    const clinicaIdRaw = item.clinicaId ?? item.ClinicaId;
    const hasNumericClinica =
      typeof clinicaIdRaw === 'number' && Number.isFinite(clinicaIdRaw) && clinicaIdRaw > 0;
    const hasStringClinica =
      typeof clinicaIdRaw === 'string' &&
      clinicaIdRaw.trim() !== '' &&
      clinicaIdRaw !== '0';

    // GUID tenant es la identidad real; clinicaId INT legacy suele ser null/0
    const clinicaId: number | string =
      tenantId ||
      (hasStringClinica ? clinicaIdRaw.trim() : null) ||
      (hasNumericClinica ? clinicaIdRaw : null) ||
      // último recurso: no usar 0 (rompe TenantId.create)
      '';

    const clinicaLabel =
      (typeof item.nombreClinica === 'string' && item.nombreClinica.trim()) ||
      (typeof item.NombreClinica === 'string' && item.NombreClinica.trim()) ||
      (tenantId != null ? `Clínica ${tenantId.slice(0, 8).toUpperCase()}` : null) ||
      (hasNumericClinica || hasStringClinica ? `Clínica ${clinicaIdRaw}` : 'Clínica sin nombre');

    return PanelPago.create(
      item.id,
      clinicaId,
      clinicaLabel,
      item.fechaPago ? new Date(item.fechaPago) : null,
      item.fechaPeriodoPago,
      item.monto,
      item.planId,
      item.medioPago,
      item.estadoPagoId || 1,
      item.estadoPagoDescripcion || 'Pendiente',
      item.deudaAcumulada || 0,
      item.medioPagoDescripcion,
      item.usuarioRegistroId,
      item.nombreArchivoComprobante,
      item.notas,
      tenantId,
      item.emailAdministrador ? String(item.emailAdministrador) : null,
      item.telefonoContacto ? String(item.telefonoContacto) : null,
      item.nombreDueno ? String(item.nombreDueno) : null
    );
  }
}
